import {
  registerUser,
  loginCognitoUser,
  logoutCognitoUser,
  resendCodeCognitoUser,
  confirmSignUpCognitoUser,
} from "api/user";
import Loader from "components/loader";
import { toast } from "react-toastify";
import { useMutation } from "react-query";
import { useEffect, useState } from "react";
import useUserSession from "zustand-store/user";
import LoginForm from "sections/login/login-form";
import SetupProfile from "components/set-up-profile";
import ConfirmSignup from "components/confirm-signup";
import { Navigate, useNavigate } from "react-router-dom";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import { Helmet } from "react-helmet-async";
import ValuePropPanel from "components/auth/value-prop-panel";
import FeatureTicker from "components/auth/feature-ticker";

const MOBILE_AVATARS = ["SM", "JT", "RL"];

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, setIsAuthenticated } = useUserSession();

  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [userPassword, setUserPassword] = useState(null);
  const [showSetupProfile, setShowSetupProfile] = useState(false);
  const [showConfirmSignup, setShowConfirmSignup] = useState(false);

  const registerUserMutation = useMutation(registerUser, {
    onSuccess: async () => {
      try {
        setShowSetupProfile(true);
        setShowConfirmSignup(false);
      } catch (error) {
        console.error("Error signing in:", error);
        navigate(isAuthenticated ? "/" : "/login", { replace: true });
      }
    },
    onError: async (error) => {
      console.error("Error registering user in backend:", error);
      try {
        await logoutCognitoUser();
        toast.error("User logged out due to registration failure", {
          position: "top-right",
        });
      } catch (logoutError) {
        console.error("Error logging out:", logoutError);
      } finally {
        navigate("/login", { replace: true });
      }
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const onSubmitLogin = async (data) => {
    try {
      setLoading(true);
      const { isSignedIn, nextStep } = await loginCognitoUser(
        data.email,
        data.password
      );

      if (isSignedIn) {
        navigate("/create-notes", { state: { showWelcome: true } });
        return;
      }

      if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
        await resendCodeCognitoUser(data.email);
        setUserEmail(data.email);
        setUserPassword(data.password);
        setShowConfirmSignup(true);
      }
    } catch (error) {
      toast.error("Error logging in user", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const onSubmitConfirmCode = async (data) => {
    try {
      setLoading(true);
      const { isSignUpComplete } = await confirmSignUpCognitoUser(
        userEmail,
        data.confirmationCode
      );
      if (isSignUpComplete) {
        await loginCognitoUser(userEmail, userPassword);
        const attributes = await fetchUserAttributes();
        const cognitoId = attributes.sub;
        const firstName = attributes["custom:firstName"] || "";
        const lastName = attributes["custom:lastName"] || "";

        registerUserMutation.mutate({
          role: "USER",
          lastName,
          firstName,
          isTrial: true,
          email: userEmail,
          isDeleted: false,
          cognitoId,
        });
      }
    } catch (error) {
      console.error("Failed to confirm signup:", error);
      toast.error("OTP verification failed. Please try again.", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async (successCallback) => {
    try {
      await resendCodeCognitoUser(userEmail);
      successCallback();
    } catch (error) {
      console.error("Failed to resend code:", error);
      toast.error("Failed to resend code. Please try again.", {
        position: "top-right",
      });
    }
  };

  useEffect(() => {
    const checkIfLoggedIn = async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Error checking user status:", error);
        setIsAuthenticated(false);
      }
    };

    checkIfLoggedIn();
  }, [setIsAuthenticated]);

  if (isAuthenticated === null) return <Loader />;

  if (isAuthenticated) {
    return (
      <Navigate to="/create-notes" replace state={{ showWelcome: true }} />
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row font-dm-sans">
      <Helmet>
        <title>Login | NirvaScribe — AI Medical Scribe</title>
        <meta
          name="description"
          content="Login to NirvaScribe app - AI note-taking tool for Healthcare Professionals."
        />
        <link
          rel="canonical"
          href="https://app.nirvascribe.com/login"
        />
      </Helmet>

      {/* Desktop: left value-prop panel — hidden once setup begins */}
      {!showSetupProfile && (
        <div className="hidden md:block md:w-1/2 shrink-0">
          <ValuePropPanel />
        </div>
      )}

      {/* Mobile: compact header — hidden once setup begins */}
      {!showSetupProfile && (
        <div className="md:hidden bg-gradient-to-b from-[#e8fbfc] via-primary to-[#7de0e6] relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[rgba(0,180,200,0.18)] blur-3xl pointer-events-none"
          />
          <div className="relative z-10 px-5 pt-4 pb-0">
            <div className="flex items-center justify-between mb-3">
              <img src="/logo-name.png" alt="NirvaScribe" className="h-7" />
              <div className="flex items-center gap-1.5">
                <div className="flex -space-x-1.5">
                  {MOBILE_AVATARS.map((init, i) => (
                    <div
                      key={i}
                      className="w-[22px] h-[22px] rounded-full bg-white/70 border-2 border-[#7de0e6] flex items-center justify-center text-[8px] text-gray-800 font-semibold"
                    >
                      {init}
                    </div>
                  ))}
                </div>
                <span className="text-[10px] text-gray-600">Aussie clinicians</span>
              </div>
            </div>
            <h1 className="text-[22px] font-bold text-gray-900 leading-tight mb-3">
              Focus on Care, Not Paperwork.
            </h1>
            <FeatureTicker dark={false} />
          </div>
        </div>
      )}

      {/* Right panel: form */}
      <div className="flex-1 md:w-1/2 bg-white md:bg-brand-off-white flex flex-col md:items-center md:justify-center md:p-6 overflow-y-auto">
        <div className="w-full md:max-w-[440px] md:bg-white md:rounded-xl md:shadow-auth-card p-6 md:p-7">
          {/* Logo on card — desktop only */}
          <div className="hidden md:block mb-5">
            <img src="/logo-name.png" alt="NirvaScribe" className="h-8" />
          </div>

          {showConfirmSignup ? (
            <ConfirmSignup
              loading={loading}
              onSubmit={onSubmitConfirmCode}
              handleResendCode={handleResendCode}
            />
          ) : showSetupProfile ? (
            <SetupProfile />
          ) : (
            <LoginForm onSubmit={onSubmitLogin} loading={loading} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
